/**
 * Costs tab — Stage 7 Phase 1 (GET /costs) and Phase 2 (GET /costs/by-app, GET /costs/untagged).
 */

import { useCallback, useState, type KeyboardEvent } from 'react';
import { CostsAwsServiceTab } from './CostsAwsServiceTab';
import { CostsByAppTab } from './CostsByAppTab';

type CostsSubTabId = 'aws' | 'blooph';

export function CostsTab(): JSX.Element {
  const [subTab, setSubTab] = useState<CostsSubTabId>('aws');

  const onAwsTabKeyDown = useCallback(
    (e: KeyboardEvent<HTMLButtonElement>): void => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        setSubTab('blooph');
      }
    },
    [],
  );

  const onBloophTabKeyDown = useCallback(
    (e: KeyboardEvent<HTMLButtonElement>): void => {
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        setSubTab('aws');
      }
    },
    [],
  );

  return (
    <div className="costs-tab">
      <h2>AWS costs</h2>
      <p className="tab-lead">
        Infrastructure spend from AWS Cost Explorer. <strong>By AWS service</strong> shows the Phase 1 view
        (service dimensions, forecast, and daily trend). <strong>By Blooph application</strong> shows Phase 2:
        costs grouped by <code className="costs-pie-inline-code">blooph-cost-app</code> with shared-pool allocation,
        plus resources missing that tag. If one sub-tab fails while the main dashboard works, check that
        observability-api exposes the matching routes.
      </p>

      <div className="costs-subtab-bar" role="tablist" aria-label="Costs view">
        <button
          type="button"
          id="costs-subtab-aws"
          role="tab"
          className={`costs-subtab ${subTab === 'aws' ? 'active' : ''}`}
          aria-selected={subTab === 'aws'}
          aria-controls="costs-subtab-panel-aws"
          tabIndex={subTab === 'aws' ? 0 : -1}
          onClick={(): void => setSubTab('aws')}
          onKeyDown={onAwsTabKeyDown}
        >
          By AWS service
        </button>
        <button
          type="button"
          id="costs-subtab-blooph"
          role="tab"
          className={`costs-subtab ${subTab === 'blooph' ? 'active' : ''}`}
          aria-selected={subTab === 'blooph'}
          aria-controls="costs-subtab-panel-blooph"
          tabIndex={subTab === 'blooph' ? 0 : -1}
          onClick={(): void => setSubTab('blooph')}
          onKeyDown={onBloophTabKeyDown}
        >
          By Blooph application
        </button>
      </div>

      <div
        id="costs-subtab-panel-aws"
        role="tabpanel"
        aria-labelledby="costs-subtab-aws"
        hidden={subTab !== 'aws'}
        className="costs-subtab-panel"
      >
        {subTab === 'aws' ? <CostsAwsServiceTab /> : null}
      </div>

      <div
        id="costs-subtab-panel-blooph"
        role="tabpanel"
        aria-labelledby="costs-subtab-blooph"
        hidden={subTab !== 'blooph'}
        className="costs-subtab-panel"
      >
        {subTab === 'blooph' ? <CostsByAppTab /> : null}
      </div>
    </div>
  );
}
